import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, HeartPulse, Activity, Zap } from 'lucide-react';
import { 
  ConstitutionType, 
  ProteinProduct, 
  CONSTITUTION_TYPES, 
  formatPrice, 
  ROUTE_PATHS 
} from '@/lib/index';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { springPresets } from '@/lib/motion';

/**
 * Mapping of Oriental Elements to Chart Colors for visual consistency
 * 地 (Earth) - Chart 3 (Warm Earth)
 * 水 (Water) - Chart 5 (Deep Blue)
 * 火 (Fire) - Chart 4 (Red/Orange)
 * 風 (Wind) - Chart 1 (Forest Teal)
 */
const elementColorMap: Record<string, string> = {
  '地': 'bg-chart-3 text-white',
  '水': 'bg-chart-5 text-white',
  '火': 'bg-chart-4 text-white',
  '風': 'bg-chart-1 text-white',
};

interface ConstitutionCardProps {
  constitution: ConstitutionType;
}

/**
 * Displays a detailed card for a specific Sasang constitution type.
 * Highlights organ balance and unique physiological traits.
 */
export function ConstitutionCard({ constitution }: ConstitutionCardProps) {
  const info = CONSTITUTION_TYPES[constitution];

  return (
    <motion.div
      whileHover={{ y: -8, scale: 1.01 }}
      transition={springPresets.gentle}
      className="h-full"
    >
      <Card className="overflow-hidden border-border/50 bg-card/60 backdrop-blur-md hover:border-primary/50 transition-all duration-300 h-full flex flex-col shadow-sm hover:shadow-xl">
        <CardHeader className="pb-4 relative">
          <div className="flex justify-between items-start mb-2">
            <CardTitle className="text-2xl font-bold tracking-tight">{info.name}</CardTitle>
            <Badge className={`${elementColorMap[info.element]} border-none px-3 py-1 text-sm font-mono shadow-sm`}>
              {info.element}
            </Badge>
          </div>
          <CardDescription className="text-sm leading-relaxed text-muted-foreground">
            {info.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-grow space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 shadow-inner">
              <div className="flex items-center gap-1.5 mb-1">
                <Activity className="w-3 h-3 text-primary" />
                <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">강한 장부 (强)</p>
              </div>
              <p className="font-bold text-lg text-primary">{info.organBalance.strong}</p>
            </div>
            <div className="p-3 rounded-xl bg-destructive/5 border border-destructive/10 shadow-inner">
              <div className="flex items-center gap-1.5 mb-1">
                <HeartPulse className="w-3 h-3 text-destructive" />
                <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">약한 장부 (弱)</p>
              </div>
              <p className="font-bold text-lg text-destructive">{info.organBalance.weak}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary/60" />
              <h4 className="text-xs font-bold uppercase tracking-widest text-foreground">체질별 특성</h4>
            </div>
            <ul className="space-y-2">
              {info.traits.map((trait, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-center gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/30 shrink-0" />
                  {trait}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>

        <CardFooter className="pt-4 border-t border-border/40">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-muted-foreground">
              기운의 방향성: <strong className="text-foreground font-semibold">{info.direction}</strong>
            </span>
            <Button variant="ghost" size="sm" className="text-xs p-0 h-auto hover:bg-transparent text-primary hover:text-primary/80" asChild>
              <Link to={ROUTE_PATHS.ABOUT} className="flex items-center gap-1">
                이론 더보기 <ArrowRight className="w-3 h-3" />
              </Link>
            </Button>
          </div>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

interface ProteinCardProps {
  product: ProteinProduct;
}

/**
 * Displays a protein product card with element-specific styling and visual hierarchy.
 */
export function ProteinCard({ product }: ProteinCardProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={springPresets.snappy}
      className="h-full"
    >
      <Card className="overflow-hidden border-border/40 bg-card shadow-sm hover:shadow-2xl transition-all duration-500 h-full flex flex-col group">
        {/* Product Image Section */}
        <div className="relative aspect-[5/4] overflow-hidden">
          <img 
            src={product.imageUrl} 
            alt={product.name} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80" />
          
          <div className="absolute top-4 left-4">
            <Badge className={`${elementColorMap[product.element]} border-none shadow-lg px-3 py-1 font-mono`}>
              {product.element}
            </Badge>
          </div>

          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-widest px-1.5 py-0.5 border border-white/20 rounded">
                {product.targetConstitution}
              </span>
            </div>
            <h3 className="text-white text-xl font-bold leading-tight">{product.name}</h3>
          </div>
        </div>

        {/* Product Content Section */}
        <CardContent className="p-6 flex-grow flex flex-col">
          <p className="text-sm font-semibold text-primary mb-3 leading-snug">
            {product.tagline}
          </p>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-6">
            {product.description}
          </p>

          <div className="mt-auto space-y-2.5">
            {product.keyBenefits.slice(0, 3).map((benefit, i) => (
              <div key={i} className="flex items-start gap-2.5 text-xs text-foreground/90">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                <span className="leading-tight">{benefit}</span>
              </div>
            ))}
          </div>
        </CardContent>

        {/* Product Footer Section */}
        <CardFooter className="px-6 py-5 flex items-center justify-between border-t border-border/40 bg-muted/20">
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground uppercase tracking-tighter">권장 소비자가</span>
            <span className="text-lg font-mono font-bold text-foreground">
              {formatPrice(product.price)}
            </span>
          </div>
          <Button asChild size="default" className="rounded-full shadow-lg group">
            <Link to={ROUTE_PATHS.PRODUCTS} className="flex items-center gap-2">
              상세정보
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

/**
 * Simple yet impactful feature card for marketing sections.
 */
export function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <motion.div
      whileHover={{ y: -10 }}
      transition={springPresets.gentle}
      className="h-full"
    >
      <Card className="h-full border-border/40 bg-card/40 backdrop-blur-sm hover:bg-card hover:border-primary/30 transition-all duration-300 shadow-sm">
        <CardHeader className="pt-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] relative group-hover:scale-110 transition-transform">
            <div className="absolute inset-0 rounded-2xl bg-primary/5 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
              {React.cloneElement(icon as React.ReactElement, { size: 28 })}
            </div>
          </div>
          <CardTitle className="text-xl font-bold tracking-tight">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-base text-muted-foreground leading-relaxed">
            {description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
